import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useAppConfig } from "config/app-config";
import { useColoredMaterials } from "hooks/colored-materials";
import type { FilterEntry } from "hooks/filters";
import { useProductSearch } from "hooks/product-search";
import { usePost } from "hooks/request";
import { useEffect, useState } from "react";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import IframeEmbed from "./ContactIframe";
// import { ContactExpertContent } from "./ContactExpertDialogContent";
import { RequestSampleDialogContent } from "./RequestSampleDialogContent";

const EMPTY_FILTERS: FilterEntry[] = [];

const SendButtonAction = styled.button({
  width: "100%",
  padding: "10px 16px",
  marginBottom: "16px",
  border: "1px solid #009FE4",
  backgroundColor: "#009FE4",
  color: "#fff",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0px 2px 0px 0px rgba(0, 0, 0, 0.04)",
  cursor: "pointer",
});

interface ContactRequestDialogProps {
  isOpen: boolean;
  dialogType: string;
  onClose: (value: boolean) => void;
  products?: ColoredMaterial[];
  selectedMaterial?: ColoredMaterial;
}
export const CONTACT_API_URL = "/v0/proxy/gigya-forms";

export const CAMPAIGN_ID = "13078661";
export const BUSINESS_ENT = "EP";

export const DIALOG_LIST = {
  CONTACT_EXPERT: "CONTACT_EXPERT",
  REQUEST_SAMPLE: "REQUEST_SAMPLE",
  REQUEST_QUOTE: "REQUEST_QUOTE",
};

export const STEP = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
};

export const ContactRequestDialog = ({
  isOpen = false,
  dialogType,
  onClose,
  products,
  selectedMaterial,
}: ContactRequestDialogProps) => {
  const [appConfig, isConfigLoading] = useAppConfig();
  const contactExpertUrl = appConfig?.contactExpert?.iframeUrl || "";
  const [dialogObject, setDialogObject] = useState<any>();
  //const [stepCompleted, setStepCompleted] = useState<number>(STEP.ONE);
  const [productSearchQuery, setProductSearchQuery] = useState<string>("");
  const {
    post,
    // loading,
    // error, // TODO: handle response
    // response
  } = usePost();

  // Fetch top products if none are provided and dialog is open
  // Load only 150 products for better performance - Autocomplete will filter client-side
  const shouldFetch =
    !products &&
    isOpen &&
    (dialogType === DIALOG_LIST.REQUEST_SAMPLE ||
      dialogType === DIALOG_LIST.REQUEST_QUOTE);
  const [allMaterials, , isLoadingMaterials] = useColoredMaterials(
    EMPTY_FILTERS, // Empty array to fetch all
    undefined,
    0,
    shouldFetch ? 150 : 0, // Fetch top 150 products when needed, 0 prevents fetch
  );

  // Server-side product search (triggered when user types)
  // Enable search when dialog is open and user has typed at least 2 characters
  const isDialogOpen =
    isOpen &&
    (dialogType === DIALOG_LIST.REQUEST_SAMPLE ||
      dialogType === DIALOG_LIST.REQUEST_QUOTE);
  const { results: searchResults, isLoading: isSearching } = useProductSearch(
    productSearchQuery,
    300, // 300ms debounce
    isDialogOpen && productSearchQuery.length >= 2, // Search when dialog is open and query is long enough
  );

  // Determine which products to use
  // Priority: 1) Search results if user is typing, 2) Provided products, 3) Fetched products
  const unfilteredProducts =
    productSearchQuery.length >= 2
      ? searchResults
      : products || (shouldFetch ? allMaterials?.results || [] : []);

  // Filter to only show products with ACTIVE color status
  const availableProducts = unfilteredProducts.filter(
    (product) => product.availability?.colorStatus === "ACTIVE",
  );

  const isLoadingProducts = isLoadingMaterials || isSearching;

  // Validation function for request details
  const isFormValid = () => {
    if (!dialogObject) return false;

    // Common required fields for all request types
    const baseValid = !!(
      dialogObject.selectedProduct &&
      dialogObject.cov_product_name?.trim() &&
      dialogObject.colorCode?.trim() &&
      dialogObject.cov_quantity_per_kg?.trim() &&
      dialogObject.cov_shipping_addr?.trim() &&
      dialogObject.cov_earliest_delivery &&
      dialogObject.cov_preferred_delivery &&
      dialogObject.cov_lead_comments?.trim()
    );

    // Additional validation for REQUEST_QUOTE
    if (dialogType === DIALOG_LIST.REQUEST_QUOTE) {
      return (
        baseValid &&
        !!(
          dialogObject.cov_potentl_yly_qty?.trim() &&
          dialogObject.cov_price_per_kg?.trim() &&
          dialogObject.cov_currency?.trim()
        )
      );
    }

    return baseValid;
  };

  const handleSendAction = () => {
    if (!dialogObject) return;

    let postData = { ...dialogObject };
    const { colorCode, selectedProduct, ...rest } = dialogObject || {};
    if (dialogType === DIALOG_LIST.REQUEST_SAMPLE) {
      postData = {
        ...rest,
        cov_campaign_id: CAMPAIGN_ID,
        cov_submission_date: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
        cov_submission_type: "Request for Sample",
        cov_business_ent: BUSINESS_ENT,
        cov_priv_pol_acc: "true", // Always true as per CDC consent
        cov_extrnl_consent: "true", // Always true as per CDC consent
        cov_lead_comments: `Color Code: ${colorCode}\n${dialogObject.cov_lead_comments || ""}`,
        cov_product_id: selectedProduct?.availability.productNumber || "",
        cov_earliest_delivery: dialogObject.cov_earliest_delivery
          ? new Date(dialogObject.cov_earliest_delivery)
              .toISOString()
              .split("T")[0]
          : "",
        cov_preferred_delivery: dialogObject.cov_preferred_delivery
          ? new Date(dialogObject.cov_preferred_delivery)
              .toISOString()
              .split("T")[0]
          : "",
      };
    } else if (dialogType === DIALOG_LIST.REQUEST_QUOTE) {
      postData = {
        ...rest,
        cov_campaign_id: CAMPAIGN_ID,
        cov_submission_date: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
        cov_submission_type: "Request for Quote",
        cov_business_ent: BUSINESS_ENT,
        cov_priv_pol_acc: "true", // Always true as per CDC consent
        cov_extrnl_consent: "true", // Always true as per CDC consent
        cov_lead_comments: `Color Code: ${colorCode}\n${dialogObject.cov_lead_comments || ""}`,
        cov_product_id: selectedProduct?.availability.productNumber || "",
        cov_earliest_delivery: dialogObject.cov_earliest_delivery
          ? new Date(dialogObject.cov_earliest_delivery)
              .toISOString()
              .split("T")[0]
          : "",
        cov_preferred_delivery: dialogObject.cov_preferred_delivery
          ? new Date(dialogObject.cov_preferred_delivery)
              .toISOString()
              .split("T")[0]
          : "",
      };
    }
    console.log(postData);
    post({ url: CONTACT_API_URL, data: postData });
    //setStepCompleted(STEP.ONE);
    onClose(false);
    setDialogObject({});
  };

  const renderDialogTitle = (key: string) => {
    switch (key) {
      case DIALOG_LIST.CONTACT_EXPERT:
        return "Contact Expert";
      case DIALOG_LIST.REQUEST_SAMPLE:
        return "Request a sample";
      case DIALOG_LIST.REQUEST_QUOTE:
        return "Request a quote";
      default:
        return "Unknow Dialog";
    }
  };

  useEffect(() => {
    //setStepCompleted(STEP.ONE);

    if (isOpen) {
      const cdcUser = getDataFromLocalstorageByKey("cdcUser");
      // Pre-fill CDC user data and selected material if provided
      const initialData: any = cdcUser
        ? {
            firstName: cdcUser.firstName || "",
            lastName: cdcUser.lastName || "",
            primaryEmail: cdcUser.email || "",
            cov_company: cdcUser.company || "",
            cov_country: cdcUser.country || "",
            cov_industry: cdcUser.extData?.work?.cov_industry || "",
            primaryPhone: cdcUser.extData?.custom?.phone || "",
            cov_job_title: cdcUser.extData?.work?.cov_job_title || "",
            cov_extrnl_consent: true,
            cov_priv_pol_acc: true,
          }
        : {};

      if (selectedMaterial) {
        initialData.selectedProduct = selectedMaterial;
        initialData.cov_product_name =
          selectedMaterial.material.publicGradeName;
        initialData.colorCode =
          selectedMaterial.color.finalColorCode ||
          selectedMaterial.color.preliminaryColorCode;
      }

      setDialogObject(initialData);
    }
  }, [dialogType, selectedMaterial, isOpen]);

  if (isConfigLoading) {
    return null;
  }

  const isIframeDialog = dialogType === DIALOG_LIST.CONTACT_EXPERT;

  return (
    <Dialog
      id="ContactRequest"
      data-cy={"ContactRequest"}
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          width: isIframeDialog ? "70vw" : "100%",
          maxWidth: isIframeDialog ? 1400 : 600,
          height: isIframeDialog ? "90vh" : "auto",
          maxHeight: "90vh",
          padding: "20px 20px 20px 20px",
        },
      }}
      onClose={() => {
        onClose(false);
        //setStepCompleted(STEP.ONE);
      }}
      aria-labelledby={dialogType}
      open={isOpen}
    >
      <DialogTitle id={dialogType} sx={{ pt: 0 }}>
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
          <Typography
            sx={{
              width: "311px",
              height: "28px",
              fontFamily: 'var(--font-family, "AktivGrotesk")',
              fontSize: "var(--title-t1-20-regular-size, 20px)",
              color: "#000",
              fontWeight: 500,
              margin: "auto",
              textAlign: "center",
            }}
            component={"div"}
          >
            {renderDialogTitle(dialogType)}
          </Typography>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => onClose(false)}
        sx={(_theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: "#000",
        })}
      >
        <CloseIcon />
      </IconButton>
      {isIframeDialog ? (
        <IframeEmbed url={contactExpertUrl} />
      ) : (
        <>
          <RequestSampleDialogContent
            dialogType={dialogType}
            requestData={dialogObject}
            onChange={setDialogObject}
            isNext={true}
            products={availableProducts}
            isLoadingProducts={isLoadingProducts}
            onSearchQueryChange={setProductSearchQuery}
            searchQuery={productSearchQuery}
          />
          <DialogActions sx={{ paddingLeft: 0 }}>
            <Box sx={{ textAlign: "center", width: "100%" }}>
              <SendButtonAction
                onClick={handleSendAction}
                disabled={!isFormValid()}
                style={{
                  opacity: isFormValid() ? 1 : 0.5,
                  cursor: isFormValid() ? "pointer" : "not-allowed",
                }}
              >
                Send
              </SendButtonAction>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
